// The Price step (docs/job-view-brief.md section 4; drawn as round 8, frame f1). Answers: what does this job sell for
// under each build, with each package and each add-on, and what does each keep? The strip is a view on the job's
// percentages (gold where this job differs from the company); the grid prices every build from the job as entered,
// As drawn and with each package on, its money rows reset by Spencer's round-6 and round-7 notes; the add-on table
// prices each add-on under each build it applies to, as the job with it on less the job without. Gross margin may be
// typed over on a build, a package cell or an add-on row; publishing is per build, per package per build, and per
// add-on, and the published selection is what the proposal shows. The arithmetic is packages/engine/src/grid.ts.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addOnCell, diffTotals, gridCell, laborLines, needByMaterial, type GridCell, type LaborLineDef, type PriceSummary, type Resolution, type Totals } from '@zerogap/engine';
import { deleteRow, insertRow, rpc, toast, updateRow, useRows, type Row } from '../../lib/api';
import { longDate, money, qty } from '../../lib/format';
import { useInventory } from '../../lib/inventory';
import { plainPricing, pricingOptions } from '../../lib/job';
import { useSession } from '../../lib/session';
import { Btn, Edit, Info, Modal, Money } from '../../ui/kit';
import { Wide } from '../../ui/wide';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-price.css';

type Pct = 'gross_margin_pct' | 'materials_markup_pct' | 'labor_contingency_pct' | 'sales_tax_pct' | 'estimate_range_down_pct' | 'estimate_range_up_pct';
const NUM = /^\d*\.?\d+$/;
const KINDS = ['contingency', 'allowance', 'markup', 'cushion', 'other'];
const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === '';
const same = (a: unknown, b: unknown) => !blank(a) && !blank(b) && Number(a) === Number(b);
const pctText = (v: unknown) => (blank(v) ? '' : String(Number(v)));
const h1 = (v: string | number | undefined) => `${Number(v ?? 0).toFixed(1)} h`;
/** Dollars without the cents when there are none ($625), as a tag reads. */
const tagMoney = (cents: number) => money(cents).replace(/\.00$/, '');
const appliesTo = (row: Row, buildId: string) => {
  const to = (row.applies_to ?? []) as string[];
  return to.length === 0 || buildId === 'job' || to.includes(buildId);
};

/** A percent typed in place: gold when it differs from its standard; shown live while typed, saved on leaving the box. */
export function PctBox({ value, ov, disabled, label, prefix, onDraft, onSave, max }: { value: string; ov?: boolean; disabled?: boolean; label: string; prefix?: string; onDraft?: (v: string | null) => void; onSave: (v: string) => Promise<unknown> | void; max?: number }) {
  const [text, setText] = useState<string | null>(null);
  const ok = (t: string) => t.trim() === '' || (NUM.test(t.trim()) && (max === undefined || Number(t) < max));
  const finish = async () => {
    if (text === null) return;
    const t = text.trim();
    if (!ok(t)) {
      toast(`${label}: type a percent${max ? ` under ${max}` : ''}, or leave it blank for the standard.`);
    } else {
      // the value shown follows the draft, so whatever was typed is saved (a save of the same figure changes nothing)
      try {
        await onSave(t);
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e));
      }
    }
    setText(null);
    onDraft?.(null);
  };
  return (
    <span className={`in num${ov ? ' ov' : ''}${text !== null && !ok(text) ? ' bad' : ''}`}>
      {prefix && <span className="pf">{prefix}</span>}
      <input
        aria-label={label}
        inputMode="decimal"
        value={text ?? value}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          onDraft?.(ok(e.target.value) ? e.target.value : null);
        }}
        onBlur={() => void finish()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setText(null);
            onDraft?.(null);
          }
        }}
      />
      <span className="pf">%</span>
    </span>
  );
}

function PubSw({ on, label, disabled, onFlip, what }: { on: boolean; label?: string; disabled?: boolean; onFlip: () => void; what: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={`${what}: ${on ? 'published' : 'not published'}`} className={`sw sm${on ? ' on' : ''}`} disabled={disabled} onClick={onFlip}>
      <i />
      <span>
        {on ? 'published' : 'not published'}
        {label ? ` · ${label}` : ''}
      </span>
    </button>
  );
}

const GP = ({ c }: { c: GridCell }) => (
  <>
    <Money cents={c.gross_profit_cents} />
    <span className="pc">{c.gross_profit_pct} %</span>
  </>
);

/** A build the grid prices: a build row, or the job as entered when it has none. */
interface B {
  id: string;
  row: Row | null;
  name: string;
  base: boolean;
  published: boolean;
}

export function Price({ ctx }: { ctx: JobCtx }) {
  const { data, asEntered, jobId } = ctx;
  const s = useSession();
  const lib = asEntered.lib!;
  const canEdit = s.canEdit('screen.price') && !ctx.locked;
  const packagesQ = useRows('package', { eq: { job_id: jobId }, order: ['sort', 'created_at'] });
  // a retired package counts nowhere (2026-10-29)
  const packages = packagesQ.rows.filter((p) => !p.retired_at);
  const [show, setShow] = useState<'internal' | 'published'>('internal');
  // the strip as typed, before it is saved: every price follows it live
  const [draft, setDraft] = useState<Partial<Record<Pct, string>>>({});
  // a gross margin typed on a row, before it is saved: build:<id>, pkg:<id>:<build>, addon:<id>:<build>
  const [rowDraft, setRowDraft] = useState<Record<string, string>>({});
  const [check, setCheck] = useState<{ kind: 'build'; b: B } | { kind: 'members' } | null>(null);

  const company = lib.pricing as unknown as Record<string, number | string | undefined>;
  const jobOv = (data.job?.pricing_overrides ?? {}) as Record<string, number | string>;
  const lineDefs = s.setting<{ lines?: LaborLineDef[] }>('price_labor_lines', {}).lines ?? [];
  const jobValue = (k: Pct | 'sub_markup_pct'): string => {
    const d = (draft as Record<string, string | undefined>)[k];
    if (d !== undefined) return blank(d) ? pctText(company[k]) : d;
    return pctText(blank(jobOv[k]) ? company[k] : jobOv[k]);
  };
  const draftPricing = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(draft)) if (v !== undefined) out[k] = blank(v) ? Number(company[k] ?? 0) : Number(v);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, lib]);
  const draftKey = JSON.stringify(draftPricing);

  // the builds, the base first in their own order; the job as entered when it has none
  const builds: B[] = data.builds.length
    ? [...data.builds.filter((b) => b.is_base), ...data.builds.filter((b) => !b.is_base)].map((b) => ({ id: b.id as string, row: b, name: b.name as string, base: !!b.is_base, published: !!b.published }))
    : [{ id: 'job', row: null, name: data.job?.scope_name ?? 'The job as entered', base: true, published: true }];
  const baseB = builds.find((b) => b.base) ?? builds[0]!;
  const addOnsById = new Map(data.addOns.map((a) => [a.id as string, a]));

  /** The job priced under a build with some add-ons on, from the job as entered, with the strip as typed. */
  const resolveFor = (b: B, addOns: Row[]): Resolution | null => {
    const opts = pricingOptions(b.row, addOns);
    if (Object.keys(draftPricing).length) opts.pricing = { ...draftPricing, ...(opts.pricing ?? {}) };
    return plainPricing(opts) ? asEntered.res : asEntered.resolveWith(opts);
  };

  // every resolution the grid reads, worked once per change
  const priced = useMemo(() => {
    const out = new Map<string, { own: Resolution | null; pkgs: Map<string, Resolution | null>; addOns: Map<string, Resolution | null> }>();
    for (const b of builds) {
      const own = resolveFor(b, []);
      const pkgs = new Map<string, Resolution | null>();
      for (const p of packages) {
        if (!appliesTo(p, b.id)) continue;
        const on = ((p.add_on_ids ?? []) as string[]).map((id) => addOnsById.get(id)).filter((a): a is Row => !!a && appliesTo(a, b.id));
        pkgs.set(p.id as string, on.length ? resolveFor(b, on) : own);
      }
      const ons = new Map<string, Resolution | null>();
      for (const a of data.addOns) if (appliesTo(a, b.id)) ons.set(a.id as string, resolveFor(b, [a]));
      out.set(b.id, { own, pkgs, addOns: ons });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asEntered, data.builds, data.addOns, packages, draftKey]);

  if (!asEntered.res) return null;

  // ---------- the margins typed on rows ----------
  const buildMargin = (b: B): string | null => {
    const d = rowDraft[`build:${b.id}`];
    if (d !== undefined) return blank(d) ? jobValue('gross_margin_pct') : d;
    const v = ((b.row?.pricing_overrides ?? {}) as Record<string, unknown>).gross_margin_pct;
    return blank(v) ? null : String(v);
  };
  const cellMargin = (key: string, stored: unknown, fallback: string): { typed: string | null; ov: boolean } => {
    const d = rowDraft[key];
    if (d !== undefined) return blank(d) ? { typed: null, ov: false } : { typed: d, ov: !same(d, fallback) };
    return blank(stored) ? { typed: null, ov: false } : { typed: String(stored), ov: !same(stored, fallback) };
  };
  const pkgMargin = (p: Row, b: B, fallback: string) => cellMargin(`pkg:${p.id}:${b.id}`, ((p.pricing_overrides ?? {}) as Record<string, Row>)[b.id]?.gross_margin_pct, fallback);
  const addOnMargin = (a: Row, b: B, fallback: string) => cellMargin(`addon:${a.id}:${b.id}`, ((a.pricing_overrides ?? {}) as Record<string, Row>)[b.id]?.gross_margin_pct, fallback);
  const draftRow = (key: string) => (v: string | null) =>
    setRowDraft((prev) => {
      const next = { ...prev };
      if (v === null) delete next[key];
      else next[key] = v;
      return next;
    });

  // ---------- saves ----------
  const saveJobPct = async (k: Pct | 'sub_markup_pct', t: string) => {
    const next = { ...jobOv };
    if (blank(t) || same(t, company[k])) delete next[k];
    else next[k] = Number(t);
    await updateRow('job', jobId, { pricing_overrides: next });
  };
  const saveBuildMargin = async (b: B, t: string) => {
    if (!b.row) return saveJobPct('gross_margin_pct', t);
    const next = { ...((b.row.pricing_overrides ?? {}) as Record<string, unknown>) };
    if (blank(t) || same(t, jobValue('gross_margin_pct'))) delete next.gross_margin_pct;
    else next.gross_margin_pct = Number(t);
    await updateRow('build', b.id, { pricing_overrides: next });
  };
  const saveCellMargin = async (table: 'package' | 'add_on', row: Row, b: B, t: string, fallback: string) => {
    const next = { ...((row.pricing_overrides ?? {}) as Record<string, Row>) };
    if (blank(t) || same(t, fallback)) delete next[b.id];
    else next[b.id] = { ...(next[b.id] ?? {}), gross_margin_pct: Number(t) };
    await updateRow(table, row.id, { pricing_overrides: next });
  };
  const flipBuild = (b: B) => b.row && void updateRow('build', b.id, { published: !b.published });
  const flipPkg = (p: Row, b: B) => {
    const pub = { ...((p.published ?? {}) as Record<string, boolean>) };
    pub[b.id] = !pub[b.id];
    void updateRow('package', p.id, { published: pub });
  };
  const makeBase = async (b: B) => {
    if (!b.row || b.base) return;
    if (!window.confirm(`Make "${b.name}" the base? The proposal leads with it, every tab prices it unless the header picks another, and a locked revision stores it.`)) return;
    try {
      await rpc('set_base_build', { p_build: b.id });
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  };

  // ---------- what is shown ----------
  const published = show === 'published';
  const shownBuilds = builds.filter((b) => !published || b.published);
  const pkgPublished = (p: Row, b: B) => !!((p.published ?? {}) as Record<string, boolean>)[b.id];
  const shownPkgs = packages.filter((p) => !published || shownBuilds.some((b) => appliesTo(p, b.id) && pkgPublished(p, b)));
  const shownAddOns = data.addOns.filter((a) => !published || a.published);

  // labor as a few lines (Spencer, 2026-10-24: Overhead, Shop, Install, the price_labor_lines setting); the class split
  // stays on the Labor tab. The rows are the setting's lines plus any class the job uses that the setting puts in no line,
  // the same rows in every column, so the eye reads across.
  const everyLabor: Pick<Totals, 'hours_by_class' | 'cents_by_class'> = { hours_by_class: {}, cents_by_class: {} };
  for (const pr of priced.values()) for (const r of [pr.own, ...pr.pkgs.values(), ...pr.addOns.values()]) for (const [id, hrs] of Object.entries(r?.totals.hours_by_class ?? {})) if (Number(hrs) !== 0) everyLabor.hours_by_class[id] = hrs;
  const laborRows = laborLines(everyLabor, lib.laborClasses, lineDefs).map((l) => l.label);
  const laborOf = (t: Totals | undefined, label: string) => (t ? laborLines(t, lib.laborClasses, lineDefs).find((l) => l.label === label) ?? null : null);

  // the package's add-ons under its name, short as the drawing has them (the first two words of each; the full names on hover)
  const pkgAddOns = (p: Row) => ((p.add_on_ids ?? []) as string[]).map((id) => addOnsById.get(id)?.name as string | undefined).filter((n): n is string => !!n);
  /** A build's short name on the add-on rows: the words before its first " · " (Cabinotch, In-House), as the drawing has them. */
  const buildShort = (b: B) => b.name.split(' · ')[0] ?? b.name;
  const shortName = (n: string) => n.split(/\s+/).slice(0, 2).join(' ').replace(/[,·]$/, '');

  // the Subs cell's tag: an estimate this build leaves out because nothing goes to its vendor
  const offTags = (res: Resolution | null) =>
    (res?.sub_lines_off ?? []).map((o) => (
      <span key={o.sub_line_id} className="tag fix" title={`"${o.scope_text}" is left out: nothing goes to the vendor under this pricing (${o.rule}).`}>
        {res!.items.find((i) => i.id === o.item_id)?.number ?? 'a sub'} {tagMoney(o.cents)} left out
      </span>
    ));

  // ---------- the builds by package ----------
  const buildRows = shownBuilds.flatMap((b) => {
    const pr = priced.get(b.id)!;
    const jobMargin = jobValue('gross_margin_pct');
    const ownTyped = rowDraft[`build:${b.id}`];
    const bm = buildMargin(b);
    const asDrawn = pr.own ? gridCell(pr.own.totals, ownTyped !== undefined ? (blank(ownTyped) ? jobMargin : ownTyped) : null) : null;
    const buildMarginNow = asDrawn?.margin_pct ?? jobMargin;
    interface Col {
      key: string;
      res: Resolution | null;
      cell: GridCell | null;
      pkg?: Row;
      ov: boolean;
      typed: string | null;
    }
    const cols: Col[] = [{ key: 'drawn', res: pr.own, cell: asDrawn, ov: !!bm && !same(bm, jobMargin), typed: bm }];
    for (const p of shownPkgs) {
      if (!appliesTo(p, b.id) || (published && !pkgPublished(p, b))) {
        cols.push({ key: p.id as string, res: null, cell: null, pkg: p, ov: false, typed: null });
        continue;
      }
      const res = pr.pkgs.get(p.id as string) ?? null;
      const m = pkgMargin(p, b, buildMarginNow);
      cols.push({ key: p.id as string, res, cell: res ? gridCell(res.totals, m.typed) : null, pkg: p, ov: m.ov, typed: m.typed });
    }
    const empty = (c: Col) => !c.cell;
    const td = (c: Col, body: React.ReactNode, cls = '') => (
      <td key={c.key} className={cls}>
        {empty(c) ? '' : body}
      </td>
    );
    const rows: React.ReactNode[] = [];
    rows.push(
      <tr key={`${b.id}-h`} className="bh">
        <td className="l">
          {b.name}
          {b.base && data.builds.length > 0 && (
            <>
              {' '}
              <span className="tag base">base</span>
            </>
          )}
          <button type="button" className="btn" onClick={() => setCheck({ kind: 'build', b })}>
            Build check
          </button>
        </td>
        {cols.map((c) =>
          td(
            c,
            <span className="lt">
              Labor{' '}
              <b>
                {h1(c.cell?.labor_hours)} · <Money cents={c.cell?.labor_cents} />
              </b>
            </span>,
          ),
        )}
        <td className="l last">
          {b.row && (
            <button type="button" className="btn" disabled={!canEdit || b.base} title={b.base ? 'This build is the base.' : 'The proposal leads with the base, and every tab prices it.'} onClick={() => void makeBase(b)}>
              Make it the base
            </button>
          )}
        </td>
      </tr>,
    );
    for (const label of laborRows) {
      rows.push(
        <tr key={`${b.id}-c-${label}`}>
          <td className="k">Labor · {label}</td>
          {cols.map((c) => {
            const l = laborOf(c.res?.totals, label);
            return td(
              c,
              <>
                <span className="hr">{h1(l?.hours)}</span>
                <Money cents={l?.cents ?? 0} />
              </>,
            );
          })}
          <td />
        </tr>,
      );
    }
    rows.push(
      <tr key={`${b.id}-mi`}>
        <td className="k">Materials charged in margin</td>
        {cols.map((c) => td(c, <Money cents={c.cell?.materials_in_cents} />))}
        <td />
      </tr>,
      <tr key={`${b.id}-mo`}>
        <td className="k">Materials priced outside margin</td>
        {cols.map((c) => td(c, <Money cents={c.cell?.materials_out_cents} />))}
        <td />
      </tr>,
      <tr key={`${b.id}-mt`} className="mt">
        <td className="k">Materials total</td>
        {cols.map((c) => td(c, <Money cents={c.cell?.materials_total_cents} />))}
        <td />
      </tr>,
      <tr key={`${b.id}-s`}>
        <td className="k">Subs</td>
        {cols.map((c) =>
          td(
            c,
            <>
              <Money cents={c.cell?.subs_cents} /> {offTags(c.res)}
            </>,
          ),
        )}
        <td />
      </tr>,
      <tr key={`${b.id}-p`} className="price">
        <td className="k">Price with range</td>
        {cols.map((c, i) =>
          td(
            c,
            <>
              <Money cents={c.cell?.price_cents} />
              <span className="rng">
                Range <Money cents={c.cell?.range_low_cents} /> – <Money cents={c.cell?.range_high_cents} />
              </span>
              {i === 0 ? (
                <PubSw on={b.published} label={b.base && data.builds.length > 0 ? 'base' : undefined} disabled={!canEdit || !b.row} onFlip={() => flipBuild(b)} what={b.name} />
              ) : (
                <PubSw on={pkgPublished(c.pkg!, b)} disabled={!canEdit} onFlip={() => flipPkg(c.pkg!, b)} what={`${c.pkg!.name} under ${b.name}`} />
              )}
            </>,
            `${i === 0 && b.base ? 'base' : ''}${c.ov ? ' ov' : ''}`,
          ),
        )}
        <td className="l last">{b.row && <PubSw on={b.published} label="the build" disabled={!canEdit} onFlip={() => flipBuild(b)} what={b.name} />}</td>
      </tr>,
      <tr key={`${b.id}-m`} className="mg">
        <td className="k">Gross margin</td>
        {cols.map((c, i) =>
          td(
            c,
            i === 0 ? (
              <>
                {c.ov && <span className="only">whole job says {jobMargin}</span>}{' '}
                <PctBox
                  key={`build-${b.id}-${pctText(((b.row?.pricing_overrides ?? {}) as Record<string, unknown>).gross_margin_pct)}`}
                  label={`Gross margin, ${b.name}`}
                  value={c.typed ?? buildMarginNow}
                  ov={c.ov}
                  max={100}
                  disabled={!canEdit}
                  onDraft={draftRow(`build:${b.id}`)}
                  onSave={(t) => saveBuildMargin(b, t)}
                />
              </>
            ) : (
              <PctBox
                key={`pkg-${c.key}-${b.id}-${pctText(((c.pkg!.pricing_overrides ?? {}) as Record<string, Row>)[b.id]?.gross_margin_pct)}`}
                label={`Gross margin, ${c.pkg!.name} under ${b.name}`}
                value={c.typed ?? buildMarginNow}
                ov={c.ov}
                max={100}
                disabled={!canEdit}
                onDraft={draftRow(`pkg:${c.key}:${b.id}`)}
                onSave={(t) => saveCellMargin('package', c.pkg!, b, t, buildMarginNow)}
              />
            ),
          ),
        )}
        <td />
      </tr>,
      <tr key={`${b.id}-g`} className="gp">
        <td className="k">Projected gross profit</td>
        {cols.map((c) => td(c, c.cell && <GP c={c.cell} />))}
        <td />
      </tr>,
    );
    return rows;
  });

  // ---------- the add-ons by build ----------
  // the add-on table's labor columns are the same lines as the grid's rows (Overhead, Shop, Install), each as the hours
  // and dollars the add-on adds under that build
  const laborBy = (t: Totals, label: string) => {
    const l = laborOf(t, label);
    return { hrs: Number(l?.hours ?? 0), cents: l?.cents ?? 0 };
  };
  interface AddOnRow {
    a: Row;
    b: B;
    cell: GridCell;
    diff: Totals;
    ov: boolean;
    typed: string | null;
    fallback: string;
    off: Resolution['sub_lines_off'];
  }
  const addOnRows: AddOnRow[][] = shownAddOns
    .map((a) =>
      shownBuilds
        .filter((b) => appliesTo(a, b.id))
        .map((b): AddOnRow | null => {
          const pr = priced.get(b.id)!;
          const withOn = pr.addOns.get(a.id as string);
          if (!withOn || !pr.own) return null;
          const fallback = gridCell(pr.own.totals).margin_pct;
          const m = addOnMargin(a, b, fallback);
          const cell = addOnCell(withOn.totals, pr.own.totals, m.typed);
          const diff = diffTotals(withOn.totals, pr.own.totals);
          const heldIds = new Set(data.items.filter((i) => i.add_on_id === a.id).map((i) => i.id as string));
          const off = (withOn.sub_lines_off ?? []).filter((o) => heldIds.has(o.item_id));
          return { a, b, cell, diff, ov: m.ov, typed: m.typed, fallback, off };
        })
        .filter((r): r is AddOnRow => !!r),
    )
    .filter((rs) => rs.length > 0);
  // a labor column shows where any add-on row has hours or dollars on it; the first two lines always, so the table keeps its shape
  const addOnLabor = laborRows.filter((label, i) => i < 2 || addOnRows.some((rs) => rs.some((r) => laborBy(r.diff, label).hrs !== 0 || laborBy(r.diff, label).cents !== 0)));
  const laborTd = (t: Totals, g: string, key: string) => {
    const l = laborBy(t, g);
    return (
      <td key={key}>
        {l.hrs === 0 && l.cents === 0 ? (
          '—'
        ) : (
          <>
            <span className="hr">{h1(l.hrs)}</span>
            <Money cents={l.cents} />
          </>
        )}
      </td>
    );
  };
  const inPkgs = (a: Row) => packages.filter((p) => ((p.add_on_ids ?? []) as string[]).includes(a.id as string)).map((p) => p.name as string);
  const itemsOf = (a: Row) => data.items.filter((i) => i.add_on_id === a.id && !i.parent_item_id).map((i) => i.number as string);
  const appliesWords = (a: Row) => {
    const to = (a.applies_to ?? []) as string[];
    if (to.length === 0 || data.builds.length === 0) return '';
    return ` · applies to ${to.map((id) => data.builds.find((b) => b.id === id)?.name ?? 'a build no longer on the job').join(', ')} only, set on Scope`;
  };
  const unverifiedIn = (a: Row) => {
    const res = priced.get(baseB.id)?.addOns.get(a.id as string);
    const held = new Set(data.items.filter((i) => i.add_on_id === a.id).map((i) => i.id as string));
    return !!res?.items.some((i) => held.has(i.id) && i.unverified.any);
  };

  // the totals under the add-on table: each build's rows added up, each at the margin shown on it
  const sums = shownBuilds
    .map((b) => {
      const rs = addOnRows.flatMap((x) => x.filter((r) => r.b.id === b.id));
      if (rs.length === 0) return null;
      const add = (f: (r: AddOnRow) => number) => rs.reduce((n, r) => n + f(r), 0);
      return {
        b,
        labor: addOnLabor.map((label) => ({ hrs: add((r) => laborBy(r.diff, label).hrs), cents: add((r) => laborBy(r.diff, label).cents) })),
        mi: add((r) => r.cell.materials_in_cents),
        mo: add((r) => r.cell.materials_out_cents),
        subs: add((r) => r.cell.subs_cents),
        price: add((r) => r.cell.price_cents),
        gp: add((r) => r.cell.gross_profit_cents),
      };
    })
    .filter((x): x is NonNullable<typeof x> => !!x);
  const lab = (l: { hrs: number; cents: number }) =>
    l.hrs === 0 && l.cents === 0 ? (
      '—'
    ) : (
      <>
        <span className="hr">{h1(l.hrs)}</span>
        <Money cents={l.cents} />
      </>
    );

  const res = asEntered.res;
  return (
    <div className="jvp">
      <div className="strip">
        {(
          [
            ['gross_margin_pct', 'Gross margin'],
            ['materials_markup_pct', 'Materials markup'],
            ['labor_contingency_pct', 'Labor contingency'],
            ['sales_tax_pct', 'Sales tax'],
          ] as [Pct, string][]
        ).map(([k, label]) => {
          const v = jobValue(k);
          const ov = !same(v, company[k] ?? 0);
          return (
            <div className="f" key={k}>
              <span className="lbl">{label}</span>
              <div className="row">
                <PctBox key={`${k}-${pctText(jobOv[k])}`} label={`${label}, this job`} value={v} ov={ov} max={k === 'gross_margin_pct' ? 100 : undefined} disabled={!canEdit} onDraft={(d) => setDraft((p) => ({ ...p, [k]: d ?? undefined }))} onSave={(t) => saveJobPct(k, t)} />
              </div>
              <span className="std">
                company <b>{pctText(company[k] ?? 0)} %</b>
                {ov ? ' · this job differs' : ''}
              </span>
            </div>
          );
        })}
        {(() => {
          const down = jobValue('estimate_range_down_pct');
          const up = jobValue('estimate_range_up_pct');
          const ovD = !same(down, company.estimate_range_down_pct ?? 0);
          const ovU = !same(up, company.estimate_range_up_pct ?? 0);
          return (
            <div className="f">
              <span className="lbl">Estimate range</span>
              <div className="row">
                <PctBox key={`d-${pctText(jobOv.estimate_range_down_pct)}`} label="Estimate range down, this job" prefix="−" value={down} ov={ovD} max={100} disabled={!canEdit} onDraft={(d) => setDraft((p) => ({ ...p, estimate_range_down_pct: d ?? undefined }))} onSave={(t) => saveJobPct('estimate_range_down_pct', t)} />
                <PctBox key={`u-${pctText(jobOv.estimate_range_up_pct)}`} label="Estimate range up, this job" prefix="+" value={up} ov={ovU} disabled={!canEdit} onDraft={(d) => setDraft((p) => ({ ...p, estimate_range_up_pct: d ?? undefined }))} onSave={(t) => saveJobPct('estimate_range_up_pct', t)} />
              </div>
              <span className="std">
                company{' '}
                <b>
                  −{pctText(company.estimate_range_down_pct ?? 0)} % · +{pctText(company.estimate_range_up_pct ?? 0)} %
                </b>
                {ovD || ovU ? ' · this job differs' : ' · down · up, under each price'}
              </span>
            </div>
          );
        })()}
        <span className="sp" />
        <div className="pub">
          <span className="lbl">Show</span>
          <div className="seg" role="group" aria-label="Show">
            <button type="button" className={show === 'internal' ? 'on' : ''} aria-pressed={show === 'internal'} onClick={() => setShow('internal')}>
              Internal
            </button>
            <button type="button" className={published ? 'on' : ''} aria-pressed={published} onClick={() => setShow('published')}>
              Published
            </button>
          </div>
        </div>
      </div>

      <div className="pg">
        {shownBuilds.length === 0 ? (
          <p className="only">No build is published yet. Switch a build's price on under Internal.</p>
        ) : (
          <Wide className="pxwrap">
            <table className="px builds" aria-label="Builds by package">
              <thead>
                <tr>
                  <th className="l">
                    Build
                    <Info>
                      Labor is the hours and dollars at cost, as three lines: Overhead (sales, design and engineering, job admin), Shop and Install; the class-by-class split is on the Labor tab. Materials charged in margin is the in-margin lines at cost with their markup and the sales tax on their cost, the margin applied; Materials priced outside margin is the lines priced on their own, with their markup and tax and no margin; Materials total adds the two. A price typed on an item (a Cabinotch box, a vendor line) rolls into the materials figures. Projected gross profit is the price less materials at cost, labor at cost, subs at cost and the sales tax. The range under each price is what the Range Estimate step hands the client. Standards {res.standards_version_name}.
                    </Info>
                  </th>
                  <th className="pk">
                    As drawn<small>no add-ons</small>
                  </th>
                  {shownPkgs.map((p) => (
                    <th key={p.id} className="pk" title={pkgAddOns(p).join(' + ')}>
                      {p.name}
                      <small>{pkgAddOns(p).map(shortName).join(' + ') || 'no add-ons yet'}</small>
                    </th>
                  ))}
                  <th className="l last">The build</th>
                </tr>
              </thead>
              <tbody>{buildRows}</tbody>
            </table>
          </Wide>
        )}

        {addOnRows.length > 0 && (
          <Wide className="pxwrap">
            <table className="px addons" aria-label="Add-ons by build">
              <thead>
                <tr>
                  <th className="l">
                    Add-on
                    <Info>An add-on is the job with it on less the job without, under that build. Which build an add-on applies to and which add-ons a package rolls in are set on Scope; here they only show.</Info>
                  </th>
                  <th className="l">On build</th>
                  {addOnLabor.map((label) => (
                    <th key={label}>{label}</th>
                  ))}
                  <th>Materials</th>
                  <th>
                    Materials
                    <br />
                    outside margin
                  </th>
                  <th>Subs</th>
                  <th>Price</th>
                  <th>Gross margin</th>
                  <th>Projected gross profit</th>
                  <th className="l last">Published</th>
                </tr>
              </thead>
              <tbody>
                {addOnRows.flatMap((rs) =>
                  rs.map((r, ix) => {
                    const a = r.a;
                    const pk = inPkgs(a);
                    return (
                      <tr key={`${a.id}-${r.b.id}`} className={ix === rs.length - 1 ? 'ao' : ''}>
                        {ix === 0 && (
                          <td className="an" rowSpan={rs.length}>
                            {a.name}
                            {unverifiedIn(a) && (
                              <>
                                {' '}
                                <span className="tag unv">unverified</span>
                              </>
                            )}
                            <small>
                              {itemsOf(a).join(', ') || 'no items yet'}
                              {appliesWords(a)}
                              {pk.length ? ` · in ${pk.join(', ')}` : ''}
                            </small>
                          </td>
                        )}
                        <td className="bn" title={r.b.name}>
                          {buildShort(r.b)}
                        </td>
                        {addOnLabor.map((label) => laborTd(r.diff, label, label))}
                        <td>
                          <Money cents={r.cell.materials_in_cents} />
                        </td>
                        <td>{r.cell.materials_out_cents ? <Money cents={r.cell.materials_out_cents} /> : '—'}</td>
                        <td>
                          <Money cents={r.cell.subs_cents} />
                          {r.off?.map((o) => (
                            <span key={o.sub_line_id} className="tag fix" title={`Left out: nothing goes to the vendor (${o.rule}).`}>
                              {o.scope_text} not priced
                            </span>
                          ))}
                        </td>
                        <td className={r.ov ? 'ov' : ''}>
                          <b>
                            <Money cents={r.cell.price_cents} sign />
                          </b>
                        </td>
                        <td>
                          <span className="mi">
                            <PctBox
                              key={`a-${a.id}-${r.b.id}-${pctText(((a.pricing_overrides ?? {}) as Record<string, Row>)[r.b.id]?.gross_margin_pct)}`}
                              label={`Gross margin, ${a.name} on ${r.b.name}`}
                              value={r.typed ?? r.fallback}
                              ov={r.ov}
                              max={100}
                              disabled={!canEdit}
                              onDraft={draftRow(`addon:${a.id}:${r.b.id}`)}
                              onSave={(t) => saveCellMargin('add_on', a, r.b, t, r.fallback)}
                            />
                          </span>
                        </td>
                        <td>
                          <GP c={r.cell} />
                        </td>
                        {ix === 0 && (
                          <td className="l last" rowSpan={rs.length}>
                            <PubSw on={!!a.published} disabled={!canEdit} onFlip={() => void updateRow('add_on', a.id, { published: !a.published })} what={a.name} />
                          </td>
                        )}
                      </tr>
                    );
                  }),
                )}
                {sums.map((x, i) => (
                  <tr key={`sum-${x.b.id}`} className={`tot${i > 0 ? ' more' : ''}`}>
                    <td className="l">{i === 0 ? 'Every add-on on' : ''}</td>
                    <td className="bn" title={x.b.name}>
                      {buildShort(x.b)}
                    </td>
                    {x.labor.map((l, k) => (
                      <td key={addOnLabor[k]}>{lab(l)}</td>
                    ))}
                    <td>
                      <Money cents={x.mi} />
                    </td>
                    <td>{x.mo ? <Money cents={x.mo} /> : '—'}</td>
                    <td>
                      <Money cents={x.subs} />
                    </td>
                    <td>
                      <Money cents={x.price} sign />
                    </td>
                    <td />
                    <td>
                      <Money cents={x.gp} />
                      <span className="pc">{x.price ? ((x.gp / x.price) * 100).toFixed(1) : '0.0'} %</span>
                    </td>
                    <td className="l last">
                      {i === 0 && (
                        <button type="button" className="btn" onClick={() => setCheck({ kind: 'members' })}>
                          Membership check
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Wide>
        )}

        <AlsoOnThePrice ctx={ctx} canEdit={canEdit} />
      </div>

      {check?.kind === 'build' && <BuildCheck ctx={ctx} b={check.b} baseB={baseB} resolveFor={resolveFor} onClose={() => setCheck(null)} />}
      {check?.kind === 'members' && <MembershipCheck ctx={ctx} packages={packages} onClose={() => setCheck(null)} />}
    </div>
  );
}

/** A build's standards beside the base's, and one item of each catalog item resolved under it beside the same item under the base. */
function BuildCheck({ ctx, b, baseB, resolveFor, onClose }: { ctx: JobCtx; b: B; baseB: B; resolveFor: (b: B, addOns: Row[]) => Resolution | null; onClose: () => void }) {
  const lib = ctx.asEntered.lib!;
  const mine = resolveFor(b, []);
  const base = resolveFor(baseB, []);
  if (!mine || !base) return null;
  const isBase = b.id === baseB.id;
  const label = (k: string) => lib.standards.find((d) => d.key === k)?.label ?? k;
  const show = (k: string, v: unknown) => {
    if (blank(v)) return '—';
    const def = lib.standards.find((d) => d.key === k);
    if (def?.data_type === 'material') return lib.materials[String(v)]?.description ?? String(v);
    if (def?.data_type === 'catalog_item') return lib.catalog[String(v)]?.name ?? String(v);
    return String(v);
  };
  const ownOv = (b.row?.global_overrides ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(ownOv), ...Object.keys((baseB.row?.global_overrides ?? {}) as Record<string, unknown>), ...Object.keys(mine.standards).filter((k) => String(mine.standards[k] ?? '') !== String(base.standards[k] ?? ''))])].sort(
    (x, y) => (lib.standards.find((d) => d.key === x)?.sort ?? 999) - (lib.standards.find((d) => d.key === y)?.sort ?? 999),
  );
  // one item of each catalog item, the first by number, as this build resolves it
  const seen = new Set<string>();
  const firsts = mine.items
    .filter((i) => !i.parent_item_id)
    .sort((x, y) => x.sort - y.sort || x.number.localeCompare(y.number))
    .filter((i) => {
      const k = i.catalog_item_id ?? `custom:${i.id}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  return (
    <Modal title={`Build check · ${b.name}`} onClose={onClose} wide>
      <div className="jv jvp-modal">
        <p className="only">{isBase ? 'This is the base: its standards are the ones the other builds are read against.' : `Its standards beside the base (${baseB.name}), and one item of each kind as each build resolves it.`}</p>
        <h4 className="lbl">Standards</h4>
        {keys.length === 0 ? (
          <p className="only">No standard differs from the base.</p>
        ) : (
          <table className="lt">
            <thead>
              <tr>
                <th>Standard</th>
                <th>Base · {baseB.name}</th>
                <th>This build</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const diff = String(mine.standards[k] ?? '') !== String(base.standards[k] ?? '');
                return (
                  <tr key={k}>
                    <td>{label(k)}</td>
                    <td>{show(k, base.standards[k])}</td>
                    <td>{diff ? <span className="held">{show(k, mine.standards[k])}</span> : <span className="only">same as the base</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <h4 className="lbl">One item of each kind</h4>
        <table className="lt">
          <thead>
            <tr>
              <th>Item</th>
              <th>Recipe</th>
              <th className="r">This build</th>
              <th className="r">Hours</th>
              <th className="r">Base</th>
              <th className="r">Hours</th>
            </tr>
          </thead>
          <tbody>
            {firsts.map((i) => {
              const o = base.items.find((x) => x.id === i.id);
              return (
                <tr key={i.id}>
                  <td>
                    <span className="mono">{i.number}</span> {i.name || i.catalog_item_name}
                    {i.add_on_id ? <small className="only"> · add-on</small> : null}
                  </td>
                  <td>{i.recipe_name ?? (i.is_custom ? 'custom' : '—')}</td>
                  <td className="r">
                    <Money cents={i.rollup_price_cents} />
                  </td>
                  <td className="r">{h1(Number(i.rollup_labor_minutes) / 60)}</td>
                  <td className="r">{o ? <Money cents={o.rollup_price_cents} /> : <span className="only">not in the base</span>}</td>
                  <td className="r">{o ? h1(Number(o.rollup_labor_minutes) / 60) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/** Items down, packages across, a mark where each item is in. */
function MembershipCheck({ ctx, packages, onClose }: { ctx: JobCtx; packages: Row[]; onClose: () => void }) {
  const { data } = ctx;
  const addOnName = (id: string) => data.addOns.find((a) => a.id === id)?.name ?? 'an add-on no longer on the job';
  const items = data.items.filter((i) => i.add_on_id && !i.parent_item_id).sort((a, b) => String(a.add_on_id).localeCompare(String(b.add_on_id)) || String(a.number).localeCompare(String(b.number)));
  const inPkg = (p: Row, item: Row) => ((p.add_on_ids ?? []) as string[]).includes(item.add_on_id as string);
  return (
    <Modal title="Membership check" onClose={onClose} wide>
      <div className="jv jvp-modal">
        <p className="only">Every item an add-on holds, and the packages it is rolled into. The base items are in every package. Which add-ons a package rolls in is set on Scope.</p>
        {items.length === 0 ? (
          <p className="only">No item is in an add-on yet.</p>
        ) : packages.length === 0 ? (
          <p className="only">This job has no package. A package is a row on the Scope table listing the add-ons it rolls in.</p>
        ) : (
          <table className="lt">
            <thead>
              <tr>
                <th>Item</th>
                <th>Add-on</th>
                {packages.map((p) => (
                  <th key={p.id} className="c">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id}>
                  <td>
                    <span className="mono">{i.number}</span> {i.name}
                  </td>
                  <td>{addOnName(i.add_on_id as string)}</td>
                  {packages.map((p) => (
                    <td key={p.id} className="c">
                      {inPkg(p, i) ? <span className="dot pk" aria-label="in" /> : <span className="dot no" aria-label="not in" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

/**
 * What the price carries beside the grid, kept from the build-up this tab replaced: the market check per foot, the
 * job-wide costs and the decision lines, and the materials whose last price paid differs from the estimating price.
 * Read under the base, as the job as entered. A price typed on an item shows on the item and rolls into the materials
 * figures; it is no line here (Spencer, 2026-10-24).
 */
function AlsoOnThePrice({ ctx, canEdit }: { ctx: JobCtx; canEdit: boolean }) {
  const { data, asEntered, jobId } = ctx;
  const s = useSession();
  const users = useRows('user');
  const res = ctx.r.res ?? asEntered.res!;
  const t = res.totals;
  const keep = 1 - Number(res.pricing.gross_margin_pct) / 100;
  const tax = Number(res.pricing.sales_tax_pct ?? 0) / 100;
  const who = (row: Row) => `${users.rows.find((u) => u.id === (row.by_user_id ?? row.created_by))?.name ?? 'the seed or an AI employee'}, ${longDate(row.created_at)}`;

  const ranges = s.setting<{ build_type: string; low: number | null; high: number | null; no_comp?: boolean }[]>('market_ranges', []);
  const range = ranges.find((x) => x.build_type === data.job?.build_type);
  const lf = Number(data.job?.linear_feet ?? 0);
  const perFoot = lf > 0 ? Math.round(t.price_cents / lf) : null;

  const inv = useInventory();
  const [showStale, setShowStale] = useState(false);
  const usedHere = needByMaterial(res.groups);
  const stale = Object.keys(usedHere)
    .map((id) => ({ m: inv.materialById.get(id), p: inv.prices.get(id), used: qty(usedHere[id]) }))
    .filter((x): x is { m: Row; p: PriceSummary; used: string } => !!x.m && !!x.p?.differs);

  const costPrice = (c: Row) => {
    const amount = Number(c.amount ?? 0) * 100;
    const mk = c.separate_pricing && !blank(c.markup_pct) ? Number(c.markup_pct) / 100 : Number(res.pricing.materials_markup_pct) / 100;
    const charged = amount * (1 + mk) + amount * tax;
    return Math.round(c.separate_pricing && c.ignore_margin ? charged : charged / keep);
  };

  return (
    <div className="also">
      <h4 className="lbl">Also on this price{ctx.build ? ` · ${ctx.build.name}` : ''}</h4>
      <table className="lt">
        <tbody>
          <tr>
            <td>
              <b>Market check</b> · {data.job?.build_type ?? 'no build type set'} · {lf > 0 ? `${lf} LF` : 'no linear feet entered'}
            </td>
            <td className="r">
              <b>{range?.no_comp ? '—' : perFoot !== null && s.can('see_prices') ? `${money(perFoot, { whole: true })} / LF` : '—'}</b>
            </td>
            <td className="r">
              {range?.no_comp ? (
                'no per-foot comp'
              ) : !range || range.low === null || range.high === null ? (
                <small>no range set</small>
              ) : perFoot === null ? (
                <small>enter linear feet on Details</small>
              ) : perFoot > range.high * 100 ? (
                <span className="warn">
                  above ${range.low.toLocaleString()}–{range.high.toLocaleString()}
                </span>
              ) : perFoot < range.low * 100 ? (
                <span className="warn">
                  below ${range.low.toLocaleString()}–{range.high.toLocaleString()}
                </span>
              ) : (
                `within $${range.low.toLocaleString()}–${range.high.toLocaleString()}`
              )}
            </td>
            <td />
          </tr>
          {data.costLines.map((c) => (
            <tr key={c.id}>
              <td>
                Job-wide cost · <Edit className="cell" disabled={!canEdit} value={c.label} onSave={(v) => updateRow('job_cost_line', c.id, { label: v || 'Job-wide cost' })} />
                <Info>
                  A cost that belongs to the whole job and no one item: tax, shipping and handling on a vendor order. Priced like a material (the materials markup and the sales tax, inside the margin), or on its own: its own markup, and outside the margin
                  when it ignores it.
                  {c.note && <div style={{ marginTop: 6 }}>{c.note}</div>}
                </Info>
                <div className="row mid wrap" style={{ gap: 10 }}>
                  <label className="check">
                    <input type="checkbox" disabled={!canEdit} checked={!!c.separate_pricing} onChange={(e) => void updateRow('job_cost_line', c.id, { separate_pricing: e.target.checked, ...(e.target.checked ? {} : { ignore_margin: false, markup_pct: null }) })} />
                    <small>Separate pricing</small>
                  </label>
                  {c.separate_pricing && (
                    <>
                      <PctBox key={`cl-${c.id}-${pctText(c.markup_pct)}`} label={`Markup, ${c.label}`} value={blank(c.markup_pct) ? pctText(res.pricing.materials_markup_pct) : pctText(c.markup_pct)} ov={!blank(c.markup_pct)} disabled={!canEdit} onSave={(v) => updateRow('job_cost_line', c.id, { markup_pct: blank(v) ? null : Number(v) })} />
                      <label className="check">
                        <input type="checkbox" disabled={!canEdit} checked={!!c.ignore_margin} onChange={(e) => void updateRow('job_cost_line', c.id, { ignore_margin: e.target.checked })} />
                        <small>Ignore margin</small>
                      </label>
                    </>
                  )}
                </div>
              </td>
              <td className="r">
                <Edit
                  className="cell short right"
                  disabled={!canEdit}
                  value={c.amount === null || c.amount === undefined ? '' : String(c.amount)}
                  placeholder="not known yet"
                  parse={(x) => (x.trim() === '' ? '' : /^-?\$?[\d,]*\.?\d+$/.test(x.trim()) ? x.replace(/[$,]/g, '') : undefined)}
                  onSave={(v) => updateRow('job_cost_line', c.id, { amount: v === '' ? null : v })}
                />
              </td>
              <td className="r">
                <Money cents={costPrice(c)} /> <small className="only">in the price</small>
              </td>
              <td className="r">
                {canEdit && (
                  <Btn small title="Remove this job-wide cost" onClick={() => window.confirm(`Remove the job-wide cost "${c.label}"?`) && void deleteRow('job_cost_line', c.id)}>
                    ×
                  </Btn>
                )}
              </td>
            </tr>
          ))}
          {data.decisionLines.map((d) => (
            <tr key={d.id}>
              <td>
                Decision ·{' '}
                <select disabled={!canEdit} value={d.kind} onChange={(e) => void updateRow('decision_line', d.id, { kind: e.target.value })}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>{' '}
                <Edit className="cell" disabled={!canEdit} value={d.label} onSave={(v) => updateRow('decision_line', d.id, { label: v || 'Decision' })} />
                <Info>Entered by {who(d)}. Added to the price at face value.</Info>
              </td>
              <td className="r">
                <Edit className="cell short right" disabled={!canEdit} value={d.amount} parse={(x) => (/^-?\$?[\d,]*\.?\d+$/.test(x.trim()) ? x.replace(/[$,]/g, '') : undefined)} onSave={(v) => updateRow('decision_line', d.id, { amount: v })} />
              </td>
              <td className="r">
                <small className="only">at face value</small>
              </td>
              <td className="r">
                {canEdit && (
                  <Btn small title="Remove this decision line" onClick={() => window.confirm(`Remove the decision line "${d.label}"? Its ${money(Math.round(Number(d.amount ?? 0) * 100))} comes off the price.`) && void deleteRow('decision_line', d.id)}>
                    ×
                  </Btn>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <button type="button" className="linklike" disabled={stale.length === 0} onClick={() => setShowStale(!showStale)}>
                {inv.loading ? 'Reading what was last paid…' : stale.length === 0 ? 'Every material here was last paid at its estimating price' : `${stale.length} ${stale.length === 1 ? 'material' : 'materials'} last paid at a different price · ${showStale ? 'hide' : 'show'}`}
              </button>
            </td>
            <td />
            <td />
            <td />
          </tr>
          {showStale &&
            stale.map(({ m, p, used }) => (
              <tr key={m.id}>
                <td style={{ paddingLeft: 24 }}>
                  <Link to={`/inventory?tab=stock&material=${m.id}`}>{m.description}</Link> <small className="only">this job uses {used} {m.unit ?? ''}</small>
                </td>
                <td className="r" colSpan={2}>
                  estimating {m.price === null ? 'none' : money(Math.round(Number(m.price) * 100))} · last paid <b>{money(Math.round(Number(p.last_paid) * 100))}</b>
                </td>
                <td />
              </tr>
            ))}
        </tbody>
      </table>
      {canEdit && (
        <div className="row mid wrap" style={{ marginTop: 8, gap: 6 }}>
          <Btn small onClick={() => void insertRow('decision_line', { job_id: jobId, kind: 'allowance', label: 'New decision line', amount: 0, by_user_id: s.user?.id ?? null })}>
            + Decision line
          </Btn>
          <Btn small onClick={() => void insertRow('job_cost_line', { job_id: jobId, label: 'Tax, shipping and handling', amount: null, sort: data.costLines.length + 1 })}>
            + Job-wide cost
          </Btn>
          <small className="only">A decision line is a contingency, an allowance or a cushion for first-time work, added at face value. A job-wide cost is a real cost that belongs to no item.</small>
        </div>
      )}
      <small className="only">
        Raw cost <Money cents={t.raw_cost_cents} /> (labor <Money cents={t.labor_cents} />, materials <Money cents={t.material_cents} />, subs <Money cents={t.sub_cents} />, sales tax <Money cents={t.tax_cents ?? 0} />) · projected gross profit{' '}
        <Money cents={t.margin_cents} /> · {Number(t.margin_pct).toFixed(1)} %
      </small>
    </div>
  );
}
